import { forwardRef, useId, type SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Select with an always-present label — BUILD-PLAN §40, §44.
 *
 * Deliberately the native `<select>`. A custom listbox would be easier to
 * style and worse to use: the native control gets the platform's own picker
 * on a phone, keyboard type-ahead on a desktop, and screen-reader behaviour
 * that no reimplementation matches. Only the chevron is ours; the rest is the
 * browser's.
 *
 * Mirrors Input's contract — same label treatment, same aria-describedby
 * wiring, same error styling — so the two never look like different form kits
 * sitting next to each other.
 */

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className' | 'onChange' | 'children'> {
  label: string;
  options: ReadonlyArray<SelectOption>;
  /** The chosen value, not the event — callers never want the event here. */
  onChange: (value: string) => void;
  error?: string | undefined;
  hint?: string | undefined;
  /** Empty first option, for when "not yet chosen" is meaningful. */
  placeholder?: string | undefined;
  /** Optional explicit id; one is generated when omitted. */
  id?: string | undefined;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, options, onChange, error, hint, placeholder, required, value, ...props },
  ref,
) {
  const generatedId = useId();
  const id = props.id ?? generatedId;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs font-medium tracking-wide text-ink">
        {label}
        {required && (
          <span className="ml-1 text-danger" aria-hidden="true">
            *
          </span>
        )}
      </label>

      <div className="relative">
        <select
          {...props}
          id={id}
          ref={ref}
          value={value}
          required={required}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          className={[
            'min-h-11 w-full appearance-none rounded-sm border bg-surface pl-3.5 pr-10 text-sm text-ink',
            'transition-colors',
            error ? 'border-danger' : 'border-line focus:border-accent',
          ].join(' ')}
        >
          {placeholder && <option value="">{placeholder}</option>}

          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Decorative: the native control still owns the interaction. */}
        <ChevronDown
          className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
          strokeWidth={1.5}
          aria-hidden="true"
        />
      </div>

      {hint && !error && (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      )}

      {error && (
        <p id={errorId} role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
});
